import {
  Box,
  Button,
  Flex,
  Skeleton,
  Tab,
  TabList,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Card } from '@ui/components/Card';
import Info from '@ui/components/Info';
import { InputCard } from '@ui/components/InputCard';
import { LPTokenIcon } from '@ui/components/LPTokenIcon';
import {
  InDesktop,
  InMobile,
  useIsDesktop,
} from '@ui/components/MobileOrDesktop';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { i18next, useTranslations } from '@ui/i18n';
import { MAXW_DESKTOP_CTA } from '@ui/utils/constants';
import { formatTokenAmount } from '@ui/utils/format';
import type { GardenType } from '@ui/utils/garden';
import {
  formatGardenApy,
  gardenTokenDict,
  getGardenName,
} from '@ui/utils/garden';
import { inMobileSelector } from '@veno-app/chakra';
import React from 'react';

import { useSupplyLcroModalStore } from '../UseLcroDirectly/useSupplyLcroModalStore';
import { GardenLogo } from './GardenLogo';
import { useGardenModalStore } from './useGardenModalStore';
import useGardenViewPlant from './useGardenViewPlant';

export type GardenViewPlantProps = {
  gardenType: GardenType;
  isWithdraw?: boolean;
};

function GardenViewPlant({ gardenType, isWithdraw }: GardenViewPlantProps) {
  const t = useTranslations();
  const {
    assetName,
    balance,
    maxAmount,
    handleInputChange,
    enteredAmount,
    inputError,
    gardenApy,
    onModeChange,
    mode,
    staked,
    isBoosted,
    onClickCTA,
    lpUsdWorth,
    stakedUsd,
    balanceUsd,
  } = useGardenViewPlant(gardenType, isWithdraw);
  const { open: openSupplyLcroModal } = useSupplyLcroModalStore();
  const openGardenModal = useGardenModalStore((s) => s.open);
  const closeGardenModal = useGardenModalStore((s) => s.close);
  const coinMode = useGardenModalStore((s) => s.coinMode);
  const gardenToken = gardenTokenDict[coinMode][gardenType];
  const activeView = useGardenModalStore((s) => s.activeView);
  const isDesktop = useIsDesktop();

  return (
    <Box
      w="full"
      minH="750px"
      backgroundImage="url(/images/plant-bg.png)"
      backgroundSize="439px 548px"
      backgroundPosition={{
        base: 'center top 350px',
        desktop: 'right -30px bottom 0px',
      }}
      backgroundRepeat="no-repeat"
    >
      <Box
        px={{
          base: 0,
          desktop: 4,
        }}
        w="full"
      >
        <GardenLogo coinMode={coinMode} gardenType={gardenType} />
        <InDesktop>
          <Text mt="24px" textAlign="center" textStyle="h4">
            {getGardeDescription(coinMode, gardenType)}
          </Text>
        </InDesktop>
        <Flex
          flexDir={{
            base: 'column',
            desktop: 'row',
          }}
          gap="22px"
          mt={6}
        >
          <Card
            w={{
              desktop: '400px',
            }}
            p={{
              base: '16px',
              desktop: '24px',
            }}
            mx="0"
            mt="0"
            mb={{ desktop: 8 }}
            sx={{
              [inMobileSelector]: {
                shadow: 'unset',
                bg: 'unset',
              },
            }}
          >
            <Tabs
              defaultIndex={isWithdraw ? 1 : 0}
              mx={{
                base: '-16px',
                desktop: '-24px',
              }}
            >
              <TabList mb={6}>
                <Tab onClick={() => onModeChange('DEPOSIT')}>
                  {t('Deposit')}
                </Tab>
                <Tab onClick={() => onModeChange('WITHDRAW')}>
                  {t('Withdraw')}
                </Tab>
              </TabList>
            </Tabs>
            <InMobile>
              <Flex flexDir="column" gap={1} mb="24px">
                <Text
                  hidden={mode === 'WITHDRAW'}
                  textAlign="center"
                  textStyle="bodySmall"
                >
                  {getGardeDescription(coinMode, gardenType)}
                </Text>
              </Flex>
            </InMobile>
            <Flex align="center" gap={1} flexDir="column">
              <Text textStyle="bodySmall">
                {mode === 'DEPOSIT'
                  ? t('Wallet {coin} Balance', {
                      coin: assetName,
                    })
                  : t('Planted {coin} Balance', {
                      coin: assetName,
                    })}
              </Text>
              <Skeleton isLoaded={!!balance && !!staked}>
                <Text textStyle="h2">
                  {formatTokenAmount(
                    mode === 'DEPOSIT' ? balance : staked,
                    4,
                    gardenType === 'TECTONIC' ? 8 : 18,
                  )}{' '}
                </Text>
              </Skeleton>
              <Skeleton
                isLoaded={balanceUsd !== undefined && stakedUsd !== undefined}
              >
                <Text textStyle={{ base: 'bodySmall', desktop: 'body' }}>
                  {mode === 'DEPOSIT'
                    ? balanceUsd?.toFixed(2) ?? '--'
                    : stakedUsd?.toFixed(2) ?? '--'}{' '}
                  USD
                </Text>
              </Skeleton>
              <Button
                mt={1}
                variant="link"
                color="text-link"
                onClick={() => {
                  if (isDesktop) {
                    closeGardenModal();
                  }
                  openSupplyLcroModal({
                    type: gardenType,
                    coinMode,
                    onBackward: () => {
                      openGardenModal({
                        type: gardenType,
                        activeView,
                        coinMode,
                      });
                    },
                  });
                }}
              >
                {t('Get more {coin}', {
                  coin: assetName,
                })}
              </Button>
            </Flex>
            <Flex flexDir="column" mt={8} gap={4}>
              <Info
                title={isBoosted ? 'APR (boosted)' : 'APR'}
                value={
                  gardenApy !== undefined ? (
                    <Box color={isBoosted ? 'garden.highlight' : 'text.normal'}>
                      {formatGardenApy(gardenApy)}
                    </Box>
                  ) : undefined
                }
              />
              {mode === 'DEPOSIT' && (
                <Info
                  title={t('Total deposit')}
                  convertedValue={
                    stakedUsd ? stakedUsd?.toFixed(2) + ' USD' : '--'
                  }
                  value={
                    staked ? (
                      <LPTokenIcon coinMode={coinMode} gardenType={gardenType}>
                        {formatTokenAmount(
                          staked,
                          4,
                          gardenType === 'TECTONIC' ? 8 : 18,
                        )}
                      </LPTokenIcon>
                    ) : undefined
                  }
                />
              )}
            </Flex>
            <Flex mt={6} flexDir="column">
              <InputCard
                className="veno-tour__garden-modal-plant-input"
                max={maxAmount}
                label={t('Input {coin} amount', {
                  coin: assetName,
                })}
                value={enteredAmount}
                onChange={handleInputChange}
                errorMessage={inputError}
                usdValue={lpUsdWorth}
              />
              {gardenType === 'TECTONIC' && (
                <Box
                  mt={2}
                  w="full"
                  p="10px 8px"
                  textStyle="body"
                  backdropFilter="auto"
                  backdropBlur="2px"
                  bgColor="#E9F9CC99"
                  borderRadius="5px"
                >
                  {t(
                    'garden-attention-{Warning}-{coin}-{gardenName}-{poolName}-{suppliedTokenName}',
                    {
                      Warning: (chunks) => (
                        <Text as="span" color="brand-warning">
                          {chunks}
                        </Text>
                      ),
                      coin: gardenToken.name,
                      gardenName: getGardenName(coinMode, gardenType),
                      suppliedTokenName: gardenToken.suppliedTokenName,
                      poolName: gardenToken.poolName,
                    },
                  )}
                </Box>
              )}
              <Button
                className="veno-tour__garden-modal-plant-button"
                onClick={onClickCTA}
                variant="primary"
                mt="14px"
                disabled={!!inputError || !enteredAmount}
                w={{ base: 'full', desktop: MAXW_DESKTOP_CTA }}
                mx={{ desktop: 'auto' }}
              >
                {mode === 'DEPOSIT'
                  ? t('Plant {coin}', {
                      coin: gardenToken.name,
                    })
                  : t('Withdraw {coin}', {
                      coin: gardenToken.name,
                    })}
              </Button>
            </Flex>
          </Card>
        </Flex>
      </Box>
    </Box>
  );
}

export const getGardeDescription = (
  coinMode: CoinMode,
  garden: GardenType,
): string => {
  const gardenName = getGardenName(coinMode, garden, false);
  const gardenToken = gardenTokenDict[coinMode][garden];
  switch (garden) {
    case 'TECTONIC':
      return i18next.t('Plant your {coin} in {gardenName} and earn VNO', {
        coin: gardenToken.name,
        gardenName: gardenName,
      });
    case 'FERRO':
    case 'VVS':
    case 'VVS_CRO':
      return i18next.t('Plant your {coin} tokens in {gardenName} to earn VNO', {
        coin: 'LP',
        gardenName: gardenName,
      });
  }
};

export default GardenViewPlant;
